import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Dashboard.css";
import { AuthContext } from "../AuthContext";
import { CartContext } from "../CartContext";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAYMENT_BASE_URL = process.env.REACT_APP_PAYMENT_BASE_URL;

function Dashboard() {
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [filteredWishlist, setFilteredWishlist] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { userInfo, logout, isLoggedIn, authFetch } = useContext(AuthContext);
  const { clearCart } = useContext(CartContext);

  // Clear the cart once a checkout has been successfully PAID.
  // Runs on landing here after payment (the payment success page redirects with
  // ?payment=success). Also falls back to checking the pending order's status in
  // case the customer returns without that flag.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get("payment") === "success";

    const finalizePaidCart = () => {
      clearCart();
      try { localStorage.removeItem("pendingOrderId"); } catch (e) { }
    };

    if (paymentSuccess) {
      finalizePaidCart();
      // strip the query flag from the URL
      params.delete("payment");
      const rest = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
      return;
    }

    // Fallback: returned without the flag — verify the pending order actually got paid
    let pendingOrderId = null;
    try { pendingOrderId = localStorage.getItem("pendingOrderId"); } catch (e) { }
    if (pendingOrderId) {
      authFetch(`${API_BASE_URL}/api/Orders/${pendingOrderId}`)
        .then((res) => (res.ok ? res.json() : null))
        .then((order) => {
          const status = (order?.orderStatus || "").toLowerCase();
          if (status === "paid") {
            finalizePaidCart();
          } else if (["failed", "cancelled", "refunded"].includes(status)) {
            // Payment didn't succeed — stop tracking it, keep the cart so they can retry
            try { localStorage.removeItem("pendingOrderId"); } catch (e) { }
          }
        })
        .catch(() => { });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [orderStatusFilter, setOrderStatusFilter] = useState("all");
  const [orderDateFilter, setOrderDateFilter] = useState("all");

  const [orderCurrentPage, setOrderCurrentPage] = useState(1);
  const [ordersPerPage] = useState(5);
  const [wishlistCurrentPage, setWishlistCurrentPage] = useState(1);
  const [wishlistItemsPerPage] = useState(5);

  const paginateOrders = (pageNumber) => setOrderCurrentPage(pageNumber);
  const paginateWishlist = (pageNumber) => setWishlistCurrentPage(pageNumber);

  const indexOfLastOrder = orderCurrentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalOrderPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const indexOfLastWishlistItem = wishlistCurrentPage * wishlistItemsPerPage;
  const indexOfFirstWishlistItem =
    indexOfLastWishlistItem - wishlistItemsPerPage;
  const currentWishlistItems = filteredWishlist.slice(
    indexOfFirstWishlistItem,
    indexOfLastWishlistItem
  );
  const totalWishlistPages = Math.ceil(
    filteredWishlist.length / wishlistItemsPerPage
  );

  const calculateSubtotal = (price, quantity) => {
    return (parseFloat(price) * parseInt(quantity)).toFixed(2);
  };

  const fetchPointsBalance = async () => {
    if (!isLoggedIn || !userInfo || userInfo.role !== "user") {
      return;
    }

    try {
      setPointsLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/Points/balance`);

      if (response.ok) {
        const data = await response.json();
        setPointsBalance(data.balance || 0);
      } else if (response.status === 404) {
        setPointsBalance(0);
      } else {
        console.error('Failed to fetch points balance');
        setPointsBalance(0);
      }
    } catch (error) {
      console.error('Error fetching points balance:', error);
      setPointsBalance(0);
    } finally {
      setPointsLoading(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Logging Out",
      text: "Are you sure you want to log out?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#4a90e2",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes",
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        navigate("/login");
      }
    });
  };

  const getOrderStatus = (order) => {
    const orderStatus = (order.status || order.orderStatus || "").toLowerCase();

    const statusMap = {
      "paid": { text: "Paid", className: "status-completed" },
      "failed": { text: "Failed", className: "status-cancelled" },
      "order processing": { text: "Order Processing", className: "status-order-processing" },
      "pending payment": { text: "Pending Payment", className: "status-pending-payment" },
      "awaiting delivery": { text: "Awaiting Delivery", className: "status-awaiting-delivery" },
      "awaiting pickup": { text: "Awaiting Pickup", className: "status-awaiting-pickup" },
      "partially shipped": { text: "Partially Shipped", className: "status-partially-shipped" },
      "out for delivery": { text: "Out for Delivery", className: "status-out-for-delivery" },
      "completed": { text: "Completed", className: "status-completed" },
      "cancelled": { text: "Cancelled", className: "status-cancelled" },
      "delayed": { text: "Delayed", className: "status-delayed" },
      "returned": { text: "Returned", className: "status-returned" },
      "refunded": { text: "Refunded", className: "status-refunded" }
    };
    return statusMap[orderStatus] || { text: "Fail", className: "status-cancelled" };
  };

  const handleOrderClick = (orderId) => {
    const order = orders.find((o) => o.orderId === orderId);
    if (order) {
      setSelectedOrder({ ...order, isLoading: true });
      setShowOrderModal(true);
      fetchOrderDetails(orderId);
    }
  };
  const handlePayment = (orderId) => {
    window.location = `${PAYMENT_BASE_URL}/payment/generate?id=${orderId}`;
  }

  const fetchOrderDetails = (orderId) => {
    setSelectedOrder((prev) => ({ ...prev, isLoading: true }));

    authFetch(`${API_BASE_URL}/api/Orders/${orderId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch order details");
        }
        return response.json();
      })
      .then((data) => {

        setSelectedOrder((prevState) => ({
          ...data,
          isLoading: false,
        }));
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Failed to load order details. Please try again later.",
        });

        setSelectedOrder((prevState) => ({
          ...prevState,
          isLoading: false,
          loadError: true,
        }));
      });
  };

  const closeOrderModal = () => {
    setShowOrderModal(false);
    setSelectedOrder(null);
  };

  const applyOrderFilters = () => {
    let filtered = [...orders];
    if (orderStatusFilter !== "all") {
      filtered = filtered.filter((order) => {
        const status = (order.status || "").toLowerCase();
        const orderStatus = (order.orderStatus || "").toLowerCase();
        return (
          status === orderStatusFilter.toLowerCase() ||
          orderStatus === orderStatusFilter.toLowerCase()
        );
      });
    }
    if (orderDateFilter === "last15") {
      const fifteenDaysAgo = new Date();
      fifteenDaysAgo.setDate(fifteenDaysAgo.getDate() - 15);
      filtered = filtered.filter(
        (order) => new Date(order.orderDate) >= fifteenDaysAgo
      );
    } else if (orderDateFilter === "last30") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(
        (order) => new Date(order.orderDate) >= thirtyDaysAgo
      );
    }
    setFilteredOrders(filtered);
  };

  const resetOrderFilters = () => {
    setOrderStatusFilter("all");
    setOrderDateFilter("all");
    setFilteredOrders(orders);
    setOrderCurrentPage(1);
  };

  useEffect(() => {
    if (!isLoggedIn || !userInfo) {
      navigate("/login");
      return;
    }

    if (userInfo.role === "user") {
      setIsLoading(true);

      fetchPointsBalance();

      authFetch(`${API_BASE_URL}/api/Orders/user/${userInfo.userId}`)
        .then((response) => {
          if (response.status === 404) {
            setOrders([]);
            setFilteredOrders([]);
            return Promise.resolve([]);
          }
          if (!response.ok) {
            throw new Error("Failed to fetch orders");
          }
          return response.json();
        })
        .then((data) => {
          const sortedOrders = data.sort(
            (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
          );
          setOrders(sortedOrders);
          setFilteredOrders(sortedOrders);
        })
        .catch((error) => {
          setError(error);
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Failed to load your orders. Please try again later.",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });

      authFetch(`${API_BASE_URL}/api/Wishlists/user/${userInfo.userId}`)
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch wishlist items");
          }
          return response.json();
        })
        .then((data) => {
          const productPromises = data.map((productId) =>
            authFetch(`${API_BASE_URL}/Products/${productId}`).then(
              (response) => {
                if (!response.ok) {
                  throw new Error(
                    `Failed to fetch product details for product ID ${productId}`
                  );
                }
                return response.json();
              }
            )
          );

          return Promise.all(productPromises);
        })
        .then((products) => {
          const productsWithImages = products.map((product) => ({
            ...product,
            mainImageUrl: `${API_BASE_URL}/products/image/${product.productID}`,
          }));
          setFilteredWishlist(productsWithImages);
        })
    }
  }, [navigate, userInfo, isLoggedIn, authFetch]);

  useEffect(() => {
    applyOrderFilters();
    setOrderCurrentPage(1);
  }, [orderStatusFilter, orderDateFilter, orders]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    const options = { hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleTimeString(undefined, options);
  };

  if (error) {
    return (
      <div className="user-dashboard-container">
        <div className="user-dashboard-sub-container">
          <div className="user-dashboard-error">
            <h3>Something went wrong</h3>
            <p>
              We're having trouble loading your dashboard. Please try again
              later.
            </p>
            <button
              className="user-dashboard-retry-btn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-dashboard-container">
      <div className="user-dashboard-sub-container">
        <div className="user-dashboard-header">
          <div className="user-dashboard-welcome-section">
            <h1 className="user-dashboard-welcome">
              Welcome back, {userInfo?.username || "user"}!
            </h1>
            <p className="user-dashboard-subtitle">
              Here's an overview of your account activity
            </p>
          </div>
          <div className="user-dashboard-actions">
            <Link to="/points" className="user-dashboard-points-btn">
              <div className="user-dashboard-points-display" data-tooltip="Redeem amazing rewards with your points!">
                <div className="points-icon">
                  <i className="fas fa-gift"></i>
                </div>
                <div className="points-info">
                  <div className="points-label">Rewards</div>
                  <div className="points-value">
                    {pointsLoading ? (
                      <div className="points-loading">
                        <i className="fas fa-spinner fa-spin"></i>
                      </div>
                    ) : (
                      <span>{pointsBalance.toLocaleString()} points</span>
                    )}
                  </div>
                </div>
              </div>
            </Link>
            <Link to="/profile/edit" className="user-dashboard-edit-profile">
              <i className="far fa-edit"></i>Edit Profile
            </Link>
            <button className="user-dashboard-logout" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>Logout
            </button>
          </div>
        </div>

        <div className="user-dashboard-content">
          {/* Orders Section with Pagination */}
          <div className="user-dashboard-section">
            <h2 className="user-dashboard-section-title">Your Orders</h2>
            <div className="user-dashboard-filter">
              <div className="filter-group">
                <label className="filter-label">Status</label>
                <select
                  className="filter-select"
                  value={orderStatusFilter}
                  onChange={(e) => setOrderStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="order processing">Order Processing</option>
                  <option value="pending payment">Pending Payment</option>
                  <option value="awaiting delivery">Awaiting Delivery</option>
                  <option value="awaiting pickup">Awaiting Pickup</option>
                  <option value="partially shipped">Partially Shipped</option>
                  <option value="out for delivery">Out for Delivery</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="delayed">Delayed</option>
                  <option value="returned">Returned</option>
                  <option value="refunded">Refunded</option>
                </select>
              </div>

              <div className="filter-group">
                <label className="filter-label">Date Range</label>
                <select
                  className="filter-select"
                  value={orderDateFilter}
                  onChange={(e) => setOrderDateFilter(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="last15">Last 15 Days</option>
                  <option value="last30">Last 30 Days</option>
                </select>
              </div>

              <button className="filter-button" onClick={resetOrderFilters}>
                Search
              </button>
            </div>
            {isLoading ? (
              <div className="user-dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Loading your orders...</p>
              </div>
            ) : filteredOrders.length > 0 ? (
              <>
                <div className="user-dashboard-orders-container">
                  <table className="user-dashboard-orders-table">
                    <thead>
                      <tr>
                        <th>Order Number</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentOrders.map((order) => {
                        const orderStatus = getOrderStatus(order);
                        return (
                          <tr key={order.orderId}>
                            <td className="user-dashboard-order-id">
                              #{order.orderNum}
                            </td>
                            <td className="user-dashboard-order-date">
                              {formatDate(order.orderDate)}
                            </td>
                            <td>
                              <span
                                className={`user-dashboard-order-status ${orderStatus.className}`}
                              >
                                {orderStatus.text}
                              </span>
                            </td>
                            <td className="user-dashboard-order-amount">
                              RM
                              {parseFloat(
                                order.finalAmount || order.totalAmount
                              ).toFixed(2)}
                            </td>
                            <td className="user-dashboard-order-action">
                              <button style={{ marginBottom: "5px", marginRight: "5px" }}
                                onClick={() => handleOrderClick(order.orderId)}
                                className="user-dashboard-view-order-btn"
                              >
                                View
                              </button>
                              {(orderStatus.text === "Failed" || orderStatus.text === "Pending Payment") && (
                                <button style={{ marginBottom: "5px" }}
                                  onClick={() => handlePayment(order.orderId)}
                                  className="user-dashboard-view-order-btn"
                                >
                                  Payment
                                </button>
                              )}

                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Order Pagination */}
                {filteredOrders.length > ordersPerPage && (
                  <div className="user-dashboard-pagination">
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateOrders(1)}
                      disabled={orderCurrentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateOrders(orderCurrentPage - 1)}
                      disabled={orderCurrentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>

                    {[...Array(totalOrderPages).keys()].map((number) => {
                      if (
                        number + 1 === 1 ||
                        number + 1 === totalOrderPages ||
                        (number + 1 >= orderCurrentPage - 1 &&
                          number + 1 <= orderCurrentPage + 1)
                      ) {
                        return (
                          <button
                            key={number + 1}
                            className={`user-dashboard-pagination-btn ${orderCurrentPage === number + 1 ? "active" : ""
                              }`}
                            onClick={() => paginateOrders(number + 1)}
                          >
                            {number + 1}
                          </button>
                        );
                      }
                      if (
                        (number + 1 === orderCurrentPage - 2 &&
                          orderCurrentPage > 3) ||
                        (number + 1 === orderCurrentPage + 2 &&
                          orderCurrentPage < totalOrderPages - 2)
                      ) {
                        return (
                          <span
                            key={number + 1}
                            className="user-dashboard-pagination-btn"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}

                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateOrders(orderCurrentPage + 1)}
                      disabled={orderCurrentPage === totalOrderPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateOrders(totalOrderPages)}
                      disabled={orderCurrentPage === totalOrderPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="user-dashboard-empty">
                {orderStatusFilter !== "all" || orderDateFilter !== "all" ? (
                  // When filters are applied but no results
                  <>
                    <p>
                      No orders found{orderStatusFilter !== "all" && ` with status "${orderStatusFilter.charAt(0).toUpperCase() + orderStatusFilter.slice(1)}"`}
                      {orderDateFilter !== "all" && ` in the selected date range`}
                    </p>
                    <button
                      className="user-dashboard-shop-now-btn"
                      onClick={resetOrderFilters}
                    >
                      Search
                    </button>
                  </>
                ) : (
                  // When user truly has no orders
                  <>
                    <p>You haven't placed any orders yet.</p>
                    <Link to="/category" className="user-dashboard-shop-now-btn">
                      Start Shopping
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Wishlist Section with Pagination */}
          <div className="user-dashboard-section">
            <h2 className="user-dashboard-section-title">Your Wishlist</h2>
            {isLoading ? (
              <div className="user-dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Loading your wishlist...</p>
              </div>
            ) : filteredWishlist.length > 0 ? (
              <>
                <div className="user-dashboard-wishlist">
                  {currentWishlistItems.map((product) => (
                    <div
                      key={product.productID}
                      className="user-dashboard-wishlist-item"
                    >
                      <Link
                        to={`/product/${product.productCode}`}
                        className="user-dashboard-wishlist-link"
                      >
                        <div className="user-dashboard-wishlist-image-container">
                          <img
                            src={product.mainImageUrl}
                            alt={product.name}
                            className="user-dashboard-wishlist-image"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/images.png";
                            }}
                          />
                          <div className="wishlist-item-price">
                            RM{product.price.toFixed(2)}
                          </div>
                        </div>
                        <div className="user-dashboard-wishlist-details">
                          <h3 className="user-dashboard-wishlist-name">
                            {product.name}
                          </h3>
                        </div>
                      </Link>
                    </div>
                  ))}
                </div>

                {/* Wishlist Pagination*/}
                {filteredWishlist.length > wishlistItemsPerPage && (
                  <div className="user-dashboard-pagination user-dashboard-wishlist-pagination">
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateWishlist(1)}
                      disabled={wishlistCurrentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateWishlist(wishlistCurrentPage - 1)}
                      disabled={wishlistCurrentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>

                    {[...Array(totalWishlistPages).keys()].map((number) => {
                      if (
                        number + 1 === 1 ||
                        number + 1 === totalWishlistPages ||
                        (number + 1 >= wishlistCurrentPage - 1 &&
                          number + 1 <= wishlistCurrentPage + 1)
                      ) {
                        return (
                          <button
                            key={number + 1}
                            className={`user-dashboard-pagination-btn ${wishlistCurrentPage === number + 1 ? "active" : ""
                              }`}
                            onClick={() => paginateWishlist(number + 1)}
                          >
                            {number + 1}
                          </button>
                        );
                      }
                      if (
                        (number + 1 === wishlistCurrentPage - 2 &&
                          wishlistCurrentPage > 3) ||
                        (number + 1 === wishlistCurrentPage + 2 &&
                          wishlistCurrentPage < totalWishlistPages - 2)
                      ) {
                        return (
                          <span
                            key={number + 1}
                            className="user-dashboard-pagination-btn"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}

                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateWishlist(wishlistCurrentPage + 1)}
                      disabled={wishlistCurrentPage === totalWishlistPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    <button
                      className="user-dashboard-pagination-btn"
                      onClick={() => paginateWishlist(totalWishlistPages)}
                      disabled={wishlistCurrentPage === totalWishlistPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="user-dashboard-empty">
                <p>Your wishlist is empty.</p>
                <Link to="/category" className="user-dashboard-shop-now-btn">
                  Browse Products
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Details Modal Section */}
      {showOrderModal && selectedOrder && (
        <div className="order-modal-overlay" onClick={closeOrderModal}>
          <div
            className="order-modal-content user-order-detail-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="user-order-detail-header">
              <div className="user-order-detail-title">
                <h2>Order Details</h2>
                <div className="user-order-detail-order-id">
                  Order #{selectedOrder.orderNum}
                </div>
              </div>
              <button className="order-modal-close" onClick={closeOrderModal}>
                &times;
              </button>
            </div>

            {selectedOrder.isLoading ? (
              <div className="user-order-detail-loading">
                <div className="loading-spinner"></div>
                <p>Loading order details...</p>
              </div>
            ) : selectedOrder.loadError ? (
              <div className="user-order-detail-error">
                <p>Failed to load order details. Please try again later.</p>
                <button
                  className="user-order-detail-btn-action user-order-detail-btn-secondary"
                  onClick={() => fetchOrderDetails(selectedOrder.orderId)}
                >
                  <i className="fas fa-sync-alt"></i> Retry
                </button>
              </div>
            ) : (
              <div className="user-order-detail-content">
                {/* Order Information Card */}
                <div className="user-order-detail-card">
                  <h2 className="user-order-detail-card-title">
                    <i className="fas fa-info-circle"></i> Order Information
                  </h2>
                  <div className="user-order-detail-info-grid">
                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">
                        Order Status
                      </span>
                      <span
                        className={`user-order-detail-status ${selectedOrder.orderStatus
                          ? `user-order-detail-status-${selectedOrder.orderStatus.toLowerCase().replace(/ /g, '-')}`
                          : `user-order-detail-status-${(
                            selectedOrder.status || ""
                          ).toLowerCase().replace(/ /g, '-')}`
                          }`}
                      >
                        {selectedOrder.orderStatus ||
                          selectedOrder.status ||
                          "New"}
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">
                        Order Date
                      </span>
                      <span className="user-order-detail-value">
                         {formatDate(selectedOrder.orderDate)} 
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">Name</span>
                      <span className="user-order-detail-value">
                        {userInfo?.username || "Guest"}
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">Email</span>
                      <span className="user-order-detail-value">
                        {selectedOrder.email || userInfo?.email}
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">Phone Number</span>
                      <span className="user-order-detail-value">
                        {selectedOrder.phoneNumber || "N/A"}
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">
                        Payment Method
                      </span>
                      <span className="user-order-detail-value">
                        {selectedOrder.paymentMethod || "Credit Card"}
                      </span>
                    </div>

                    <div className="user-order-detail-info-item">
                      <span className="user-order-detail-label">
                        Order Comment
                      </span>
                      <span className="user-order-detail-value">
                        {selectedOrder.orderComment || "-"}
                      </span>
                    </div>
                  </div>

                  {/* E-Invoice Section */}
                  <div
                    className="user-order-detail-card-title"
                    style={{ marginTop: "30px" }}
                  >
                    <i className="fas fa-file-invoice"></i> E-Invoice
                  </div>

                  <div className="user-order-detail-info-item">
                    <span className="user-order-detail-label">
                      E-Invoice Requested
                    </span>
                    <span className="user-order-detail-value">
                      {selectedOrder.eInvoiceRequested ? "Yes" : "No"}
                    </span>
                  </div>

                  {selectedOrder.eInvoiceRequested && (
                    <>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">Buyer Type</span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceBuyerType || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          {selectedOrder.eInvoiceBuyerType === "Company"
                            ? "Company Name"
                            : "Full Name"}
                        </span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceName || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">TIN</span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceTIN || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          {selectedOrder.eInvoiceBuyerType === "Company"
                            ? "Business Registration No."
                            : "NRIC"}
                        </span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceBRN || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          SST Registration No.
                        </span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceSSTNumber || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          E-Invoice Email
                        </span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoiceEmail || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          E-Invoice Phone
                        </span>
                        <span className="user-order-detail-value">
                          {selectedOrder.eInvoicePhone || "-"}
                        </span>
                      </div>
                      <div className="user-order-detail-info-item">
                        <span className="user-order-detail-label">
                          E-Invoice Address
                        </span>
                        <span
                          className="user-order-detail-value"
                          style={{ whiteSpace: "pre-wrap" }}
                        >
                          {selectedOrder.eInvoiceAddress || "-"}
                        </span>
                      </div>
                    </>
                  )}

                  {/* Price Summary Section */}
                  <div
                    className="user-order-detail-card-title"
                    style={{ marginTop: "30px" }}
                  >
                    <i className="fas fa-money-bill"></i> Price Summary
                  </div>

                  <div
                    className="user-order-detail-info-item"
                    style={{
                      borderBottom: "1px solid #e1e4e8",
                      paddingBottom: "10px",
                    }}
                  >
                    <span className="user-order-detail-label">
                      Total Amount
                    </span>
                    <span className="user-order-detail-value">
                      RM{parseFloat(selectedOrder.totalAmount).toFixed(2)}
                    </span>
                  </div>

                  <div
                    className="user-order-detail-info-item"
                    style={{
                      backgroundColor: "rgb(250, 222, 195)",
                      padding: "10px",
                      borderRadius: "5px",
                      marginTop: "10px",
                      border: "1px dashed rgb(255, 150, 51)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        className="user-order-detail-label"
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <i
                          className="fas fa-tag"
                          style={{
                            color: "rgb(179, 98, 22)",
                            marginRight: "8px",
                          }}
                        ></i>
                        Discount Applied
                      </span>
                      <span
                        className="user-order-detail-value"
                        style={{
                          color: "rgb(179, 98, 22)",
                          fontWeight: "bold",
                        }}
                      >
                        - RM
                        {parseFloat(selectedOrder.discountAmount || 0).toFixed(
                          2
                        )}
                      </span>
                    </div>
                  </div>

                  <div
                    className="user-order-detail-info-item"
                    style={{
                      marginTop: "15px",
                      backgroundColor: "#f5f5f5",
                      padding: "15px",
                      borderRadius: "5px",
                    }}
                  >
                    <span
                      className="user-order-detail-label"
                      style={{ fontSize: "16px", fontWeight: "600" }}
                    >
                      Final Amount
                    </span>
                    <span className="user-order-detail-amount">
                      RM
                      {parseFloat(
                        selectedOrder.finalAmount || selectedOrder.totalAmount
                      ).toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Shipping Information Card*/}
                <div className="user-order-detail-card">
                  <h2 className="user-order-detail-card-title">
                    <i className={selectedOrder.isPickup ? "fas fa-store" : "fas fa-shipping-fast"}></i>
                    {selectedOrder.isPickup ? "Pickup Information" : "Shipping Information"}
                  </h2>

                  {selectedOrder.isPickup ? (
                    <div className="user-order-detail-info-item" style={{
                      backgroundColor: '#e3f2fd',
                      padding: '15px',
                      borderRadius: '8px',
                      border: '2px solid #2196f3',
                      textAlign: 'center'
                    }}>
                      <i className="fas fa-store" style={{ marginRight: '8px', color: '#2196f3', fontSize: '1.2rem' }}></i>
                      <strong style={{ color: '#1976d2', fontSize: '1rem' }}>
                        This order will be collected at the store
                      </strong>
                    </div>
                  ) : (
                    <>
                      <div className="user-order-detail-info-grid">
                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">
                            Contact Person Name
                          </span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingContactName || "N/A"}
                          </span>
                        </div>
                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">
                            Contact Number
                          </span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingContactNumber || "N/A"}
                          </span>
                        </div>
                        <div className="user-order-detail-info-item" style={{ gridColumn: "1 / -1" }}>
                          <span className="user-order-detail-label">
                            Shipping Address
                          </span>
                          <div className="user-order-detail-address user-order-detail-value"
                            style={{ marginBottom: "15px" }}>
                            {[
                              selectedOrder.unitBlock,
                              selectedOrder.shippingAddress1,
                              selectedOrder.shippingAddress2,
                              selectedOrder.shippingZipCode,
                              selectedOrder.shippingCity,
                              selectedOrder.shippingState
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        </div>
                      </div>

                      <div className="user-order-detail-info-grid">
                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">
                            Unit Block
                          </span>
                          <span className="user-order-detail-value">
                            {selectedOrder.unitBlock || "N/A"}
                          </span>
                        </div>

                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">
                            Address Line 1
                          </span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingAddress1 || "N/A"}
                          </span>
                        </div>

                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">
                            Address Line 2
                          </span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingAddress2 || "N/A"}
                          </span>
                        </div>

                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">Zip Code</span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingZipCode || "N/A"}
                          </span>
                        </div>

                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">City</span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingCity || "N/A"}
                          </span>
                        </div>

                        <div className="user-order-detail-info-item">
                          <span className="user-order-detail-label">State</span>
                          <span className="user-order-detail-value">
                            {selectedOrder.shippingState || "N/A"}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Order Items Card */}
                <div className="user-order-detail-card user-order-detail-items-card">
                  <h2 className="user-order-detail-card-title">
                    <i className="fas fa-shopping-cart"></i> Order Items
                  </h2>

                  <div className="user-order-detail-table-container">
                    <table className="user-order-detail-table">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th className="user-order-detail-quantity">
                            Quantity
                          </th>
                          <th className="user-order-detail-price">Price</th>
                          <th className="user-order-detail-subtotal">
                            Subtotal
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.orderItems &&
                          selectedOrder.orderItems.length > 0 ? (
                          selectedOrder.orderItems.map((item, index) => (
                            <tr key={item.orderItemId || index}>
                              <td className="user-order-detail-product-name">
                                <div>{item.productName || "Product"}</div>
                                {item.productCode && (
                                  <div className="user-order-detail-product-code">
                                    Code: {item.productCode}
                                  </div>
                                )}
                                {item.variantDetails &&
                                  item.variantDetails.length > 0 && (
                                    <div className="user-order-detail-variant-details">
                                      <ul className="user-order-detail-variant-list">
                                        {item.variantDetails.map(
                                          (variant, idx) => (
                                            <li
                                              key={idx}
                                              className="user-order-detail-variant-item"
                                            >
                                              <span className="user-order-detail-variant-name">
                                                {variant.specificationName}:
                                              </span>
                                              <span className="user-order-detail-variant-value">
                                                {variant.specificationValue}
                                              </span>
                                            </li>
                                          )
                                        )}
                                      </ul>
                                    </div>
                                  )}
                              </td>
                              <td className="user-order-detail-quantity">
                                {item.quantity}
                              </td>
                              <td className="user-order-detail-price">
                                RM{parseFloat(item.price).toFixed(2)}
                              </td>
                              <td className="user-order-detail-subtotal">
                                RM{calculateSubtotal(item.price, item.quantity)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" style={{ textAlign: "center" }}>
                              No items found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
